import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { clockIn, type TimeJob, type TimeEntry } from '../../data/scheduleTimeTracking';
import InstallationClockIn from './InstallationClockIn';
import { serviceKeys } from '@/data/service';
import ScheduleTimeTracking from '../../pages/ScheduleTimeTracking';
import useTimeLaunchState from '../../hooks/useTimeLaunchState';
import { useToasts } from '../../ui/ToastProvider';
import { safeErrorMessage } from '../../utils/appErrors';
import { syncSimpleInOutJobComment } from './simpleInOutJobComment';
import ClockedInSplitButton from './ClockedInSplitButton';
import './JobTimeButton.css';
import './clockActions.css';

export default function JobTimeButton({ home = false, job, locationId, serviceLocationIds = [] }: {
  home?: boolean; job?: TimeJob; locationId?: string | null; serviceLocationIds?: string[];
}) {
  const { state, authorizationKey, ready, preview, data } = useTimeLaunchState();
  const client = useQueryClient();
  const { push } = useToasts();
  const [open, setOpen] = React.useState(false);
  const [installationClock, setInstallationClock] = React.useState(false);
  const request = React.useRef<string | null>(null);
  const active = data?.active;
  const sameJob = Boolean(active && job && active.job_kind === job.kind && active.job_id === job.id);
  const eligible = job?.kind === 'installation' ? ['confirmed', 'in_progress'].includes(job.status)
    : job?.kind === 'service' && !['closed', 'cancelled'].includes(job.status);
  const visible = Boolean(data && (home ? active || data.can_clock_in_home
    : sameJob || eligible && (locationId === data.context.location_id || serviceLocationIds.includes(data.context.location_id))));
  const mutation = useMutation({
    mutationFn: async () => {
      if (!data || !job) throw new Error('Refresh the job before clocking in.');
      request.current ??= crypto.randomUUID();
      return clockIn(request.current, job, data.context.location_id);
    },
    onSuccess: async entry => {
      request.current = null;
      await Promise.all([client.invalidateQueries({ queryKey: ['schedule-time-launch'] }), client.invalidateQueries({ queryKey: ['schedule-time-history'] }),
        client.invalidateQueries({ queryKey: ['labor-calendar'] }), client.invalidateQueries({ queryKey: ['job-time-history'] }), client.invalidateQueries({ queryKey: serviceKeys.all })]);
      push({ type: 'success', message: `Clocked in to ${job?.code}.` });
      syncSimpleInOutJobComment(client);
    },
    onError: async error => {
      push({ type: 'error', message: safeErrorMessage(error, 'Clock-in was not confirmed. Try again.') });
      await client.invalidateQueries({ queryKey: ['schedule-time-launch'] });
    },
  });
  React.useEffect(() => { setOpen(false); setInstallationClock(false); request.current = null; }, [authorizationKey, job?.id]);
  if (ready && !preview && state.isError && !data) return <button type="button" className="btn btn-outline" onClick={() => { void state.refetch(); }}>Retry time tracking</button>;
  if (!visible || !data) return null;
  const otherJob = Boolean(active && !home && !sameJob);
  const clockingOut = Boolean(active && (home || sameJob));
  const label = mutation.isPending ? 'Clocking in…' : clockingOut ? 'Clock out' : 'Clock in';
  const content = home ? <>
    <svg className="job-time-control__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" />
      {active ? <rect x="9.5" y="9.5" width="5" height="5" rx="0.5" /> : <path d="M10.5 9v6l4.5-3z" />}
    </svg>
    <span className="job-time-control__label">{label}</span>
  </> : label;
  return <div className={`job-time-control${home ? ' job-time-control--home' : ''}`} data-active={active ? 'true' : undefined}>
    {/* Clocked in on Home: the job and Clock out are one split button, the job half opening the job (2026-10-28). */}
    {home && active ? <ClockedInSplitButton entry={active} onClockOut={() => setOpen(true)}>{content}</ClockedInSplitButton> : (
      // Keep a record header's phone menu open: closing it would unmount this button and the dialog it opens.
      <button type="button" className={`btn ${clockingOut ? 'danger clock-action--out' : 'primary clock-action--in'}`} disabled={mutation.isPending || otherJob} data-rh-menu-keep-open
        title={otherJob ? `Clock out of ${active?.job_code} before starting this job.` : undefined}
        aria-busy={mutation.isPending} onClick={() => { if (home || sameJob) setOpen(true); else if (job?.kind === 'installation') setInstallationClock(true); else mutation.mutate(); }}>
        {content}
      </button>
    )}
    {otherJob ? <span className="text-xs fg-muted">Clocked in to {active?.job_code}</span> : null}
    {open ? <ScheduleTimeTracking key={authorizationKey} context={data.context} authorizationKey={authorizationKey}
      start={dayjs().startOf('week').subtract(1, 'week').format('YYYY-MM-DD')}
      end={dayjs().add(1, 'day').format('YYYY-MM-DD')} initialEntry={active ?? undefined} onClose={() => setOpen(false)} /> : null}
    {installationClock && job?.kind === 'installation' ? <InstallationClockIn key={authorizationKey} job={job} locationId={data.context.location_id}
      onClose={() => setInstallationClock(false)} onStarted={() => {
        void Promise.allSettled(['schedule-time-launch', 'schedule-time-history', 'labor-calendar', 'job-time-history'].map(key => client.invalidateQueries({ queryKey: [key] })));
        push({ type: 'success', message: `Clocked in to ${job.code}.` }); syncSimpleInOutJobComment(client);
      }} /> : null}
  </div>;
}
